import { useEffect, useEffectEvent, useRef, useState } from 'react';

/** Reveal one page of rows at a time; scrolling the sentinel near the bottom
 *  reveals the next. The full list is already in memory (client-side filtered),
 *  so this is pure DOM windowing — no extra fetch. */
export const WINDOW_PAGE_SIZE = 20;

export interface IWindowedList {
  /** How many rows are currently revealed. */
  visibleCount: number;
  /** Whether more rows remain to reveal. */
  hasMore: boolean;
  /** Attach to the sentinel `<li>` rendered at the end of the visible window
   *  (only while `hasMore`). Intersecting it reveals the next page. */
  sentinelRef: React.RefObject<HTMLLIElement | null>;
}

/**
 * Windowing for an in-memory list, shared by the Activities & Issues feed and
 * the dashboard Activity Alerts card.
 *
 * `total` is the full row count; `resetKey` is a signature of the VIEW (a
 * filter/tab/day/schedule string) — when it changes, the window snaps back to
 * the first page. Never the rows array itself: a live refetch mints a new array
 * for the same view, and a reset on it yanks a scrolled reader back to the top.
 * The reset is done during render (the React-recommended way to adjust state on
 * a changed input) so the window never paints a stale page-count for a frame.
 *
 * The reveal is an `useEffectEvent` so the IntersectionObserver reads the latest
 * `total` without listing it as a dependency — the observer is created once per
 * `hasMore` transition, never torn down and recreated on every page reveal.
 */
export function useWindowedList(total: number, resetKey: string): IWindowedList {
  const [visibleCount, setVisibleCount] = useState(WINDOW_PAGE_SIZE);

  const [seenResetKey, setSeenResetKey] = useState(resetKey);
  if (resetKey !== seenResetKey) {
    setSeenResetKey(resetKey);
    setVisibleCount(WINDOW_PAGE_SIZE);
  }

  const hasMore = visibleCount < total;

  const revealNextPage = useEffectEvent(() => {
    setVisibleCount((count) => Math.min(count + WINDOW_PAGE_SIZE, total));
  });

  const sentinelRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!hasMore || !sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) revealNextPage();
      },
      { rootMargin: '200px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore]);

  return { visibleCount, hasMore, sentinelRef };
}
